import { useState } from 'react'
import { Loader2, Check } from 'lucide-react'
import * as LucideIcons from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { PluginFeatureGroup } from '../types'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@tendbot/ui'

interface FeatureSelectionModalProps {
  pluginName: string
  pluginIcon: string
  pluginColor: string
  featureGroups: PluginFeatureGroup[]
  onConnect: (selectedFeatures: string[]) => void
  onClose: () => void
  connecting: boolean
  /** Show BETA badge + unverified-app notice (Google Workspace until CASA verification passes) */
  beta?: boolean
}

function FeatureIcon({ name, className }: { name: string; className?: string }) {
  const Icon = (LucideIcons as unknown as Record<string, React.ComponentType<{ size?: number; className?: string }>>)[name]
  if (!Icon) return null
  return <Icon size={18} className={className} />
}

export default function FeatureSelectionModal({
  pluginIcon,
  pluginColor,
  featureGroups,
  onConnect,
  onClose,
  connecting,
  beta,
}: FeatureSelectionModalProps) {
  const { t } = useTranslation('plugins')

  const [selected, setSelected] = useState<Set<string>>(() => {
    const defaults = new Set<string>()
    for (const g of featureGroups) {
      if (g.defaultEnabled) defaults.add(g.id)
    }
    return defaults
  })

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const canConnect = selected.size > 0 && !connecting

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent accent="gold">
        <div className="flex items-center gap-3 pr-8 mb-1">
          <div
            className="w-10 h-10 rounded-xl border border-gold/20 flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${pluginColor}15` }}
          >
            <FeatureIcon name={pluginIcon} className="text-ivory" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <DialogTitle className="text-lg">{t('featureSelection.heading')}</DialogTitle>
              {beta && (
                <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-500/10 text-amber-300 border border-amber-500/30">
                  {t('featureSelection.betaBadge')}
                </span>
              )}
            </div>
            <DialogDescription className="text-xs">
              {t('featureSelection.subtitle')}
            </DialogDescription>
          </div>
        </div>

        <div className="space-y-2 mt-4">
          {featureGroups.map(group => {
            const isSelected = selected.has(group.id)
            return (
              <button
                key={group.id}
                onClick={() => toggle(group.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                  isSelected
                    ? 'glass-card border-gold/30 glow-gold'
                    : 'glass-card border-transparent hover:border-gold/10'
                }`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                  isSelected ? 'bg-gold/15' : 'bg-slate-mid'
                }`}>
                  <FeatureIcon
                    name={group.icon}
                    className={isSelected ? 'text-gold' : 'text-pearl/50'}
                  />
                </div>
                <div className="flex-1 text-left">
                  <div className={`text-sm font-medium ${isSelected ? 'text-ivory' : 'text-pearl/70'}`}>
                    {group.label}
                  </div>
                  <div className="text-xs text-pearl/60">{group.description}</div>
                </div>
                <div className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-all ${
                  isSelected ? 'bg-gold border-gold' : 'border-pearl/20'
                }`}>
                  {isSelected && <Check size={12} className="text-midnight" />}
                </div>
              </button>
            )
          })}
        </div>

        {beta && (
          <div className="mt-4 rounded-lg border border-amber-500/20 bg-amber-500/[0.06] px-3 py-2.5">
            <p className="text-amber-200/90 text-[11px] leading-snug">
              {t('featureSelection.betaNotice')}
            </p>
          </div>
        )}

        <p className="text-pearl/55 text-xs text-center mt-4">
          {t('featureSelection.addLaterHint')}
        </p>

        <button
          onClick={() => onConnect([...selected])}
          disabled={!canConnect}
          className="mt-4 w-full flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-midnight font-semibold px-6 py-2.5 rounded-xl transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {connecting ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              {t('featureSelection.connecting')}
            </>
          ) : (
            t('featureSelection.connectButton')
          )}
        </button>
      </DialogContent>
    </Dialog>
  )
}
